/**
 * Persistent-storage permission and usage.
 *
 * The browser may evict IndexedDB under storage pressure. `persist()` asks it not
 * to — but only after a real user gesture, and framed as offline reliability
 * rather than a permission prompt for its own sake. A refusal is fine: the app
 * keeps working, it is simply more evictable.
 */

export interface StorageEstimate {
  usedBytes: number;
  quotaBytes: number;
  persisted: boolean;
}

export async function isPersisted(): Promise<boolean> {
  if (!navigator.storage?.persisted) return false;
  try {
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

/** Call from a click, never on load. Returns whether the browser agreed. */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function estimateStorage(): Promise<StorageEstimate | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const estimate = await navigator.storage.estimate();
    return {
      usedBytes: estimate.usage ?? 0,
      quotaBytes: estimate.quota ?? 0,
      persisted: await isPersisted(),
    };
  } catch {
    return null;
  }
}

/** Human-readable size. Learners should not have to read bytes. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
